import React, { useEffect, useState } from 'react';
import { api, moneyFull, moneyExact } from '../lib/api';
import { AlertTriangle, CheckCircle2, Clock, Pencil, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ACCOUNT_CODES = ['Income', 'Profit', 'Owner', 'Tax', 'OpEx'];
const ACCOUNT_LABELS = {
  Income: 'Indie Income',
  Profit: 'Indie Profit',
  Owner:  'Indie Owner Pay',
  Tax:    'Indie Tax',
  OpEx:   'Indie Opex',
};

// Cash Runway card — advisor Spec 2.
//   Runway = actual bank balances (Relay, manual entry) ÷ trailing 3-mo avg OpEx.
//   Manual bank balances are preferred because the Profit First sheet tracks
//   *allocated* on-paper balances that drift from Relay reality.
export default function CashRunwayCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  // Threshold editor
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [draftThreshold, setDraftThreshold] = useState('3');
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Balances editor
  const [editingBalances, setEditingBalances] = useState(false);
  const [draftBalances, setDraftBalances] = useState({});
  const [draftNote, setDraftNote] = useState('');
  const [savingBalances, setSavingBalances] = useState(false);

  const load = () => {
    api.get('/cash-runway')
      .then((r) => {
        setData(r.data);
        setDraftThreshold(String(r.data.threshold_months ?? 3));
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load cash runway.'));
  };

  useEffect(() => { load(); }, []);

  const saveThreshold = async () => {
    const n = parseFloat(draftThreshold);
    if (!Number.isFinite(n) || n <= 0 || n > 24) {
      setErr('Threshold must be 0–24 months.');
      return;
    }
    setSavingThreshold(true);
    try {
      await api.patch('/cash-runway/threshold', { threshold_months: n });
      setEditingThreshold(false);
      setErr('');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to update threshold.');
    } finally {
      setSavingThreshold(false);
    }
  };

  const startEditBalances = () => {
    const seed = data?.per_account_balances || {};
    const initial = {};
    ACCOUNT_CODES.forEach((c) => { initial[c] = seed[c] != null ? String(seed[c]) : ''; });
    setDraftBalances(initial);
    setDraftNote(data?.manual_entry?.note || '');
    setEditingBalances(true);
    setErr('');
  };

  const saveBalances = async () => {
    const parsed = {};
    for (const c of ACCOUNT_CODES) {
      const raw = draftBalances[c];
      if (raw === '' || raw == null) continue;
      const n = parseFloat(raw);
      if (!Number.isFinite(n) || n < 0) {
        setErr(`Invalid balance for ${ACCOUNT_LABELS[c]}.`);
        return;
      }
      parsed[c] = n;
    }
    if (Object.keys(parsed).length === 0) {
      setErr('Enter at least one account balance.');
      return;
    }
    setSavingBalances(true);
    try {
      await api.patch('/cash-runway/balances', { balances: parsed, note: draftNote.trim() || null });
      setEditingBalances(false);
      setErr('');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save balances.');
    } finally {
      setSavingBalances(false);
    }
  };

  if (!data) {
    return (
      <div data-testid="cash-runway-card" className="bg-white border-t-4 border-indie-navy p-6">
        <div className="label-editorial">Cash runway</div>
        <div className="mt-4 text-sm text-indie-ink/60">{err || 'Loading…'}</div>
      </div>
    );
  }

  const {
    status, runway_months, threshold_months, operating_cash, operating_cash_source,
    monthly_opex_avg, months_covered, per_account_balances,
    source_kind, manual_entry, allocation_gap, sheet_total_cash,
  } = data;

  const statusPalette = {
    green:   { icon: CheckCircle2,  color: 'text-indie-evergreen', bg: 'bg-indie-evergreen/[0.08]', border: 'border-indie-evergreen', label: 'RESERVE HEALTHY' },
    amber:   { icon: Clock,          color: 'text-indie-cobalt',   bg: 'bg-indie-cobalt/[0.06]',    border: 'border-indie-cobalt',   label: 'WATCH ZONE' },
    red:     { icon: AlertTriangle,  color: 'text-indie-neg-dark', bg: 'bg-indie-neg-dark/[0.06]',  border: 'border-indie-neg-dark', label: 'BELOW RESERVE' },
    unknown: { icon: Clock,          color: 'text-indie-ink/60',   bg: 'bg-indie-cream',            border: 'border-indie-navy',     label: 'NO DATA' },
  }[status] || {};
  const StatusIcon = statusPalette.icon || Clock;

  const runwayDisplay = runway_months != null ? `${runway_months.toFixed(1)} months` : '—';
  const targetDelta = runway_months != null ? runway_months - threshold_months : null;

  const freshnessStatus = manual_entry?.freshness?.status || 'unknown';
  const daysAgo = manual_entry?.freshness?.days_ago;
  const freshPalette = {
    fresh:   { bg: 'bg-indie-evergreen/[0.08]', text: 'text-indie-evergreen', label: 'FRESH' },
    aging:   { bg: 'bg-indie-cobalt/[0.08]',    text: 'text-indie-cobalt',    label: 'AGING' },
    stale:   { bg: 'bg-indie-neg-dark/[0.08]',  text: 'text-indie-neg-dark',  label: 'STALE' },
    unknown: { bg: 'bg-indie-cream',            text: 'text-indie-ink/60',    label: 'SHEET' },
  }[freshnessStatus];

  return (
    <div data-testid="cash-runway-card" className={`bg-white border-t-4 ${statusPalette.border} p-6`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="label-editorial">Cash runway</div>
          <div className="text-xl font-semibold text-indie-navy mt-1">Months of OpEx cover</div>
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="cash-runway-source" className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${freshPalette.bg} ${freshPalette.text}`}>
            {source_kind === 'manual' ? freshPalette.label : 'FROM SHEET'}
          </div>
          <div data-testid="cash-runway-status" className={`flex items-center gap-2 px-3 py-1.5 ${statusPalette.bg} ${statusPalette.color}`}>
            <StatusIcon size={16} />
            <span className="text-xs font-semibold uppercase tracking-wider">{statusPalette.label}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <div className="label-editorial text-indie-ink/60">Runway</div>
          <div data-testid="cash-runway-value" className="text-4xl font-semibold text-indie-navy num-tabular mt-1">{runwayDisplay}</div>
          {targetDelta != null && (
            <div className={`text-xs mt-1 num-tabular ${targetDelta >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
              {targetDelta >= 0 ? '+' : ''}{targetDelta.toFixed(1)} vs {threshold_months.toFixed(1)}-mo target
            </div>
          )}
        </div>

        <div>
          <div className="label-editorial text-indie-ink/60">Operating cash</div>
          <div className="text-2xl font-semibold text-indie-navy num-tabular mt-1">{operating_cash != null ? moneyExact(operating_cash) : '—'}</div>
          <div className="text-[11px] text-indie-ink/60 mt-1 leading-snug">
            {operating_cash_source || 'No balances yet'}
            {manual_entry?.entered_at && (
              <> · updated {new Date(manual_entry.entered_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              {daysAgo != null ? ` (${daysAgo.toFixed(daysAgo < 1 ? 1 : 0)}d ago)` : ''}
              {manual_entry.entered_by ? ` by ${manual_entry.entered_by.split('@')[0]}` : ''}</>
            )}
          </div>
        </div>

        <div>
          <div className="label-editorial text-indie-ink/60">Avg monthly OpEx</div>
          <div className="text-2xl font-semibold text-indie-navy num-tabular mt-1">{monthly_opex_avg != null ? moneyFull(monthly_opex_avg) : '—'}</div>
          <div className="text-[11px] text-indie-ink/60 mt-1 num-tabular">
            {months_covered?.length ? `Trailing ${months_covered.length}: ${months_covered.join(', ')}` : 'No P&L months synced'}
          </div>
        </div>
      </div>

      {/* Balances editor / display */}
      {editingBalances ? (
        <div data-testid="cash-runway-balances-editor" className="mt-5 pt-4 border-t border-indie-navy/10 space-y-2.5">
          <div className="text-xs uppercase tracking-wider text-indie-navy font-semibold">Update actual bank balances (Relay)</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {ACCOUNT_CODES.map((c) => (
              <div key={c}>
                <div className="text-[10px] uppercase tracking-wider text-indie-ink/60 mb-0.5">{ACCOUNT_LABELS[c]}</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-indie-ink/60 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={draftBalances[c] || ''}
                    onChange={(e) => setDraftBalances({ ...draftBalances, [c]: e.target.value })}
                    data-testid={`cash-runway-balance-${c.toLowerCase()}`}
                    className="w-full border-b border-indie-navy/30 focus:border-indie-navy focus:outline-none text-sm num-tabular bg-transparent py-1"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}
          </div>
          <input
            type="text"
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
            data-testid="cash-runway-balances-note"
            placeholder="Optional note (e.g., 'Relay screenshot 7/1')"
            className="w-full border border-indie-navy/30 px-2 py-1 text-sm mt-2"
            maxLength={140}
          />
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={saveBalances}
              disabled={savingBalances}
              data-testid="cash-runway-balances-save"
              className="px-3 py-1.5 bg-indie-navy text-indie-yellow text-[11px] font-semibold uppercase tracking-wider disabled:opacity-60"
            >
              {savingBalances ? 'Saving…' : 'Save balances'}
            </button>
            <button
              type="button"
              onClick={() => { setEditingBalances(false); setErr(''); }}
              data-testid="cash-runway-balances-cancel"
              className="p-1.5 text-indie-ink/60 hover:text-indie-navy"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-5 pt-4 border-t border-indie-navy/10 flex flex-wrap items-start justify-between gap-3 text-xs text-indie-ink/60">
          <div className="flex-1 min-w-0">
            {per_account_balances && Object.keys(per_account_balances).length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 num-tabular">
                <span className="uppercase tracking-wider text-[10px]">By account</span>
                {ACCOUNT_CODES.filter((c) => per_account_balances[c] != null).map((c) => (
                  <span key={c} className="text-indie-ink/70">
                    {c} <span className="text-indie-navy font-medium">{moneyExact(per_account_balances[c])}</span>
                  </span>
                ))}
              </div>
            )}
            {allocation_gap != null && Math.abs(allocation_gap) > 100 && (
              <div className="text-[11px] text-indie-ink/50 mt-1.5">
                Profit First sheet allocates {moneyExact(sheet_total_cash)} (Δ {allocation_gap > 0 ? '+' : ''}{moneyExact(allocation_gap)} vs. actual) — money assigned on paper but not yet swept between accounts.
              </div>
            )}
            {manual_entry?.note && (
              <div className="text-[11px] text-indie-ink/50 mt-1 italic">&ldquo;{manual_entry.note}&rdquo;</div>
            )}
          </div>
          {user?.is_admin && (
            <button
              type="button"
              onClick={startEditBalances}
              data-testid="cash-runway-balances-edit"
              className="inline-flex items-center gap-1 border border-indie-navy text-indie-navy px-3 py-1 text-[11px] uppercase tracking-wider font-semibold hover:bg-indie-navy hover:text-indie-cream"
            >
              <Pencil size={11} /> Update balances
            </button>
          )}
        </div>
      )}

      {/* Threshold row */}
      <div className="mt-3 pt-3 border-t border-indie-navy/5 flex items-center justify-end gap-2 text-xs text-indie-ink/60">
        <span className="uppercase tracking-wider text-[11px]">Target</span>
        {editingThreshold ? (
          <div className="flex items-center gap-2">
            <input
              type="number" min="0.5" max="24" step="0.5"
              value={draftThreshold}
              onChange={(e) => setDraftThreshold(e.target.value)}
              data-testid="cash-runway-threshold-input"
              className="w-20 border border-indie-navy px-2 py-1 text-sm num-tabular"
            />
            <span className="text-[11px]">mo</span>
            <button type="button" onClick={saveThreshold} disabled={savingThreshold}
              data-testid="cash-runway-threshold-save"
              className="px-3 py-1 bg-indie-navy text-indie-yellow text-[11px] font-semibold uppercase tracking-wider disabled:opacity-60">
              {savingThreshold ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setEditingThreshold(false); setDraftThreshold(String(threshold_months)); setErr(''); }}
              data-testid="cash-runway-threshold-cancel"
              className="p-1 text-indie-ink/60 hover:text-indie-navy" aria-label="Cancel">
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <span className="text-indie-navy font-semibold num-tabular">{threshold_months.toFixed(1)} months</span>
            {user?.is_admin && (
              <button type="button" onClick={() => setEditingThreshold(true)}
                data-testid="cash-runway-threshold-edit"
                className="p-1 text-indie-ink/60 hover:text-indie-navy" aria-label="Edit reserve target">
                <Pencil size={13} />
              </button>
            )}
          </>
        )}
      </div>
      {err && <div data-testid="cash-runway-error" className="mt-3 text-xs text-indie-neg-dark">{err}</div>}
    </div>
  );
}
